import React, { useState, useEffect, useMemo } from "react";
import styled, { keyframes } from "styled-components";
import {
  useNavigateWithCommandClick,
  createNavigationHandler
} from "../hooks/useNavigateWithCommandClick";
import { API_ENDPOINTS } from "../config/api";

import Button from "../components/Button";
import PageHeader from "../components/PageHeader";
import Logo from "../components/Logo";
import BotDisplay from "../components/BotDisplay";
import { Card, CardTitle } from "../components/Card";
import { ErrorState, LoadingState } from "../components/CommonStates";

// Styled Components
const BotSelectionContainer = styled.div`
  background: #f8f9fa;
  min-height: 100vh;
  width: 100%;
  padding-top: 80px; /* Account for fixed header */

  @media (max-width: 768px) {
    padding-top: 0; /* Remove padding on mobile since header is relative */
  }
`;

const spinAnimation = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #000000;
  border-radius: 50%;
  animation: ${spinAnimation} 1s linear infinite;
  margin-bottom: 1rem;
`;

const Banner = styled.p`
  background: rgb(255, 242, 165);
  color: rgb(75, 64, 0);
  font-weight: 600;
  padding: 1rem;
  border: 0.125rem solid rgb(255, 217, 0);
  text-align: center;
  border-radius: 0.75rem;
  margin-top: 2rem;
`;

const BotSelectionContent = styled.main`
  padding: 2rem;
  max-width: 60rem;
  margin: 0 auto;
  gap: 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const BotsGrid = styled.div`
  display: grid;
  grid-template-columns: ${(props) =>
    props.$botCount === 1 ? "1fr" : "repeat(2, 1fr)"};
  gap: 1.5rem;
  align-items: stretch;
  width: 100%;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const BotCard = styled(Card)`
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  height: 100%;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  }

  @media (max-width: 768px) {
    padding: 1rem;
  }

  @media (max-width: 480px) {
    padding: 0.75rem;
  }
`;

const BotCardFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: auto;
  padding-top: 1rem;
`;

const NoBots = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: #6c757d;
  max-width: 40rem;
  margin: 0 auto;

  h3 {
    color: #495057;
    margin-bottom: 1rem;
  }
`;

function BotSelection({ user, onSelectBot, onLogout }) {
  const navigate = useNavigateWithCommandClick();
  const [availableBots, setAvailableBots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch available bots on component mount
  useEffect(() => {
    const fetchBots = async () => {
      try {
        const response = await fetch(
          `${API_ENDPOINTS.bots}?username=${encodeURIComponent(user.username)}`
        );
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        setAvailableBots(data.bots || []);
      } catch (error) {
        console.error("Error fetching bots:", error);
        setError("Failed to load available bots. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchBots();
  }, [user.username]);

  const handleBotSelect = (botId, event) => {
    const selectedBot = availableBots.find((bot) => bot.id === botId);
    if (selectedBot) {
      onSelectBot(selectedBot, event);
    }
  };

  const handleLogout = () => {
    if (window.confirm("Are you sure you want to logout?")) {
      onLogout();
    }
  };

  // Create header actions based on user permissions
  const headerActions = useMemo(() => {
    const actions = [
      {
        label: "Settings",
        onClick: createNavigationHandler("/settings", navigate)
      }
    ];

    if (user?.isAdmin) {
      actions.unshift({
        label: "Admin",
        onClick: createNavigationHandler("/admin", navigate)
      });
    }

    return actions;
  }, [navigate, user?.isAdmin]);

  if (loading) {
    return (
      <BotSelectionContainer>
        <PageHeader
          title={<Logo />}
          onLogout={handleLogout}
          rightActions={headerActions}
        />
        <LoadingState>
          <LoadingSpinner />
        </LoadingState>
      </BotSelectionContainer>
    );
  }

  return (
    <BotSelectionContainer>
      <PageHeader
        title={<Logo />}
        onLogout={handleLogout}
        rightActions={headerActions}
      />

      {error && <ErrorState>{error}</ErrorState>}

      <BotSelectionContent>
        <Card $fullWidth>
          <CardTitle $center>Welcome to Propel Chat!</CardTitle>
          <Banner>
            BETA: We're working on this feature with a small group of testers.
            Thank you for trying it out!
          </Banner>
        </Card>

        {/* Bots Section */}
        <BotsGrid $botCount={availableBots.length}>
          {availableBots.map((bot) => (
            <BotCard
              key={bot.id}
              onClick={(event) => handleBotSelect(bot.id, event)}
            >
              <BotDisplay bot={bot} variant="card" />
              <BotCardFooter>
                <Button variant="chat" customColor={bot.color || "#4CAF50"}>
                  {bot.vision ? "Analyze images" : "Start chat"}
                </Button>
              </BotCardFooter>
            </BotCard>
          ))}
        </BotsGrid>

        {availableBots.length === 0 && !loading && (
          <NoBots>
            <h3>No assistants available</h3>
            <p>Please check your connection and try again.</p>
            <Button variant="retry" onClick={() => window.location.reload()}>
              Retry
            </Button>
          </NoBots>
        )}
      </BotSelectionContent>
    </BotSelectionContainer>
  );
}

export default BotSelection;
